import React, { useEffect, useRef, useState } from "react";
import { api, errMsg, formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Store as StoreIcon, Download, FileSpreadsheet, FileText, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function PartnersPage({ kind }) {
  const isStore = kind === "stores";
  const label = isStore ? "Toko Binaan" : "Distributor";
  const path = isStore ? "stores" : "distributors";
  const empty = {
    name: "", contact: "", address: "", pic_name: "", pic_wa: "", pic_email: "",
    opening_hours: "", notes: "", initial_stock: [],
  };

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(new Set());
  const [editStock, setEditStock] = useState([]); // stok titipan yang sedang diedit (mode edit toko)
  const [stockLoading, setStockLoading] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const load = () => api.get(`/${path}`).then((r) => { setItems(r.data); setSelected(new Set()); });
  useEffect(() => {
    load();
    if (isStore) api.get("/products").then((r) => setProducts(r.data));
  }, [path, isStore]);

  const tf = useTableFilter(items, {
    searchKeys: ["name", "contact", "address"],
    defaultSort: { key: "name", dir: "asc" },
  });

  // Saat masuk mode edit toko → fetch stok titipan terbaru
  const openEdit = async (it) => {
    setEditing(it);
    setForm({ ...empty, ...it });
    setOpen(true);
    if (isStore) {
      setStockLoading(true);
      try {
        const { data } = await api.get(`/stores/${it.id}/stock`);
        setEditStock(data.items || []);
      } catch (e) { toast.error("Gagal ambil stok: " + errMsg(e)); setEditStock([]); }
      finally { setStockLoading(false); }
    }
  };

  const updateEditStockLine = (i, key, val) => setEditStock((arr) => {
    const next = [...arr];
    next[i] = { ...next[i], [key]: val };
    return next;
  });

  const save = async () => {
    try {
      if (editing) {
        const { initial_stock, ...rest } = form;
        await api.put(`/${path}/${editing.id}`, rest);
        // Simpan perubahan stok titipan (untuk toko saja)
        if (isStore) {
          const items = editStock
            .filter((s) => Number(s.qty) > 0 || s.in_store) // include existing yang dibuat 0 untuk dihapus
            .map((s) => ({ product_id: s.product_id, qty: Number(s.qty || 0), unit_price: Number(s.unit_price || 0) }));
          if (items.length > 0) {
            const { data } = await api.put(`/stores/${editing.id}/stock`, { items });
            toast.success(`${label} tersimpan · ${data.updated} stok diupdate${data.removed?`, ${data.removed} dihapus`:""}`);
          } else {
            toast.success(`${label} tersimpan`);
          }
        } else {
          toast.success(`${label} tersimpan`);
        }
      } else {
        const payload = { ...form };
        if (isStore) {
          payload.initial_stock = (form.initial_stock || []).filter((it) => it.product_id && it.qty > 0)
            .map((it) => ({ product_id: it.product_id, qty: Number(it.qty), unit_price: Number(it.unit_price) }));
        }
        await api.post(`/${path}`, payload);
        toast.success(`${label} tersimpan${form.initial_stock?.length ? " dengan stok awal" : ""}`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(`Hapus ${label}?`)) return;
    try { await api.delete(`/${path}/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Hapus ${selected.size} ${label} sekaligus? Data terkait (stok titipan, user mitra) juga ikut terhapus.`)) return;
    try {
      const { data } = await api.post(`/${path}/bulk-delete`, { ids: Array.from(selected) });
      toast.success(`${data.deleted} ${label} terhapus`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(tf.rows.map((r) => r.id)));
    else setSelected(new Set());
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const addStockLine = () => setForm((f) => ({ ...f, initial_stock: [...(f.initial_stock || []), { product_id: "", qty: 1, unit_price: 0 }] }));
  const removeStockLine = (i) => setForm((f) => ({ ...f, initial_stock: f.initial_stock.filter((_, idx) => idx !== i) }));
  const updateStockLine = (i, key, val) => setForm((f) => {
    const arr = [...(f.initial_stock || [])];
    arr[i] = { ...arr[i], [key]: val };
    if (key === "product_id") {
      const p = products.find((pp) => pp.id === val);
      if (p) arr[i].unit_price = p.retail_price;
    }
    return { ...f, initial_stock: arr };
  });

  // --- Exports ---
  const exportRows = () => tf.rows.map((r, i) => ({
    No: i + 1,
    Nama: r.name || "",
    Kontak: r.contact || "-",
    Alamat: r.address || "-",
  }));
  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = exportRows();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 20 }, { wch: 60 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, label);
      const fname = `${label.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Excel diunduh (${rows.length} baris)`);
    } catch (e) { toast.error("Gagal ekspor Excel: " + (e.message || e)); }
  };

  const downloadTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      const template = [
        {
          name: "Contoh Toko A",
          contact: "021-1234567",
          address: "Jl. Contoh No. 1, Jakarta",
          pic_name: "Budi Santoso",
          pic_wa: "081234567890",
          stok_produk: products[0]?.name || "HOTTO",
          stok_qty: 50,
          stok_harga: products[0]?.retail_price || 250000,
        },
        {
          name: "Contoh Toko B (tanpa stok awal)",
          contact: "021-7654321",
          address: "Jl. Contoh No. 2, Bandung",
          pic_name: "Siti Aminah",
          pic_wa: "081298765432",
          stok_produk: "",
          stok_qty: "",
          stok_harga: "",
        },
      ];
      const ws = XLSX.utils.json_to_sheet(template, {
        header: ["name", "contact", "address", "pic_name", "pic_wa", "stok_produk", "stok_qty", "stok_harga"],
      });
      ws["!cols"] = [{ wch: 30 }, { wch: 18 }, { wch: 50 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 10 }, { wch: 14 }];
      // Info sheet
      const info = XLSX.utils.aoa_to_sheet([
        ["PANDUAN UPLOAD TOKO BINAAN"],
        [""],
        ["Kolom yang wajib diisi:"],
        ["- name        : Nama toko / cabang (wajib, unik)"],
        [""],
        ["Kolom opsional:"],
        ["- contact     : No telp / email toko"],
        ["- address     : Alamat lengkap"],
        ["- pic_name    : Nama Person In Charge di toko"],
        ["- pic_wa      : Nomor WA PIC"],
        [""],
        ["Kolom Stok Awal (opsional, per baris hanya 1 produk):"],
        ["- stok_produk : Nama produk PERSIS sesuai master (case-insensitive)"],
        ["- stok_qty    : Jumlah stok awal titipan"],
        ["- stok_harga  : Harga jual di toko ini (opsional, default = retail_price)"],
        [""],
        ["Catatan:"],
        ["1. Toko yang namanya sudah ada akan DILEWATI (dedup case-insensitive)."],
        ["2. User Mitra otomatis dibuat: username `mitra-<slug-nama>`, password default `123456`."],
        ["3. Stok awal masuk sebagai titipan di toko dan tidak memengaruhi kas atau stok gudang."],
        ["4. Untuk 1 toko dengan banyak produk, isi 1 baris toko dulu, lalu tambah baris terpisah dengan nama toko sama + hanya kolom stok_* (tapi baris tambahan akan dihitung sebagai duplikat & dilewati — sebaiknya update stok via halaman toko setelah upload)."],
      ]);
      info["!cols"] = [{ wch: 90 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Toko");
      XLSX.utils.book_append_sheet(wb, info, "Panduan");
      XLSX.writeFile(wb, "Template_Upload_Toko_Binaan.xlsx");
      toast.success("Template diunduh — buka sheet 'Panduan' untuk instruksi");
    } catch (e) { toast.error("Gagal buat template: " + (e.message || e)); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (rows.length === 0) return toast.error("File kosong");
      if (!window.confirm(`Impor ${rows.length} baris ${label}? Data yang sudah ada akan dilewati.`)) {
        e.target.value = ""; return;
      }
      const { data } = await api.post(`/stores/bulk-import`, { rows });
      toast.success(`Berhasil: ${data.created} baru, ${data.skipped} dilewati, ${data.stock_added} stok awal`);
      if (data.errors?.length) {
        setTimeout(() => toast.warning(`Ada ${data.errors.length} peringatan — cek konsol`, { duration: 5000 }), 500);
        console.warn("Import warnings:", data.errors);
      }
      load();
    } catch (err) { toast.error("Gagal upload: " + (err.message || errMsg(err))); }
    finally { e.target.value = ""; }
  };

  const exportPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable").catch(() => {});
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(`Daftar ${label} — Hotto Branch Cianjur`, 14, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Total ${tf.rows.length} entri · Dicetak ${new Date().toLocaleDateString("id-ID")}`, 14, 22);
      const rows = exportRows();
      if (doc.autoTable) {
        doc.autoTable({
          head: [["No", "Nama", "Kontak", "Alamat"]],
          body: rows.map((r) => [r.No, r.Nama, r.Kontak, r.Alamat]),
          startY: 28,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [4, 120, 87], textColor: 255 },
        });
      } else {
        let y = 30;
        doc.setFont("helvetica", "bold");
        doc.setFillColor(4, 120, 87); doc.setTextColor(255, 255, 255);
        doc.rect(14, y - 5, 269, 7, "F");
        doc.text("No", 16, y); doc.text("Nama", 30, y); doc.text("Kontak", 120, y); doc.text("Alamat", 170, y);
        y += 8; doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0);
        rows.forEach((r) => {
          if (y > 195) { doc.addPage(); y = 20; }
          doc.text(String(r.No), 16, y);
          doc.text((r.Nama || "").substring(0, 55), 30, y);
          doc.text((r.Kontak || "").substring(0, 25), 120, y);
          doc.text((r.Alamat || "").substring(0, 65), 170, y);
          y += 6;
        });
      }
      doc.save(`${label.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success(`PDF diunduh (${rows.length} baris)`);
    } catch (e) { toast.error("Gagal ekspor PDF: " + (e.message || e)); }
  };

  const allSelected = tf.rows.length > 0 && tf.rows.every((r) => selected.has(r.id));

  return (
    <>
      <PageHeader
        title={label}
        subtitle={isStore ? "Toko rekanan yang menerima penyaluran" : "Sumber pembelian stok Anda"}
        testId={`page-${path}`}
        actions={
          <div className="flex gap-2 flex-wrap">
            {isStore && (
              <>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload}/>
                <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template" className="border-blue-400 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2"/> Template
                </Button>
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-upload-stores" className="border-purple-400 text-purple-700 hover:bg-purple-50">
                  <Upload className="w-4 h-4 mr-2"/> Upload Excel
                </Button>
              </>
            )}
            <Button variant="outline" onClick={exportExcel} data-testid={`btn-export-excel-${path}`} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <FileSpreadsheet className="w-4 h-4 mr-2"/> Excel
            </Button>
            <Button variant="outline" onClick={exportPDF} data-testid={`btn-export-pdf-${path}`} className="border-rose-400 text-rose-700 hover:bg-rose-50">
              <FileText className="w-4 h-4 mr-2"/> PDF
            </Button>
            <Button onClick={()=>{setEditing(null);setForm(empty);setOpen(true);}} data-testid={`btn-add-${path}`} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Plus className="w-4 h-4 mr-2"/> Tambah {label}
            </Button>
          </div>
        }
      />

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[var(--hotto-border)] flex items-center justify-between gap-2 flex-wrap">
          <TableToolbar tf={tf} placeholder={`Cari ${label.toLowerCase()}...`} testId={`search-${path}`}/>
          {isStore && selected.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md" data-testid="bulk-toolbar">
              <span className="text-xs text-red-800 font-medium">{selected.size} terpilih</span>
              <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="btn-bulk-delete" className="h-7 text-xs">
                <Trash2 className="w-3 h-3 mr-1"/> Hapus Terpilih
              </Button>
              <Button size="sm" variant="ghost" onClick={()=>setSelected(new Set())} className="h-7 text-xs">Batal</Button>
            </div>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {isStore && (
                <TableHead className="w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="check-all"/>
                </TableHead>
              )}
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="name">Nama</SortHeader>
              <SortHeader tf={tf} col="contact">Kontak</SortHeader>
              <SortHeader tf={tf} col="address">Alamat</SortHeader>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && (
              <TableRow><TableCell colSpan={isStore?6:5} className="text-center py-8 text-slate-500">Belum ada {label}.</TableCell></TableRow>
            )}
            {tf.rows.map((it, idx) => (
              <TableRow key={it.id} className={`table-row-hover ${selected.has(it.id)?"bg-red-50/40":""}`}>
                {isStore && (
                  <TableCell><Checkbox checked={selected.has(it.id)} onCheckedChange={()=>toggleOne(it.id)} data-testid={`check-${it.id}`}/></TableCell>
                )}
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-medium">
                  {isStore ? (
                    <button onClick={()=>nav(`/stores/${it.id}`)} className="hover:text-[var(--hotto-primary)] text-left">
                      <StoreIcon className="w-4 h-4 inline mr-1"/>{it.name}
                    </button>
                  ) : it.name}
                </TableCell>
                <TableCell className="text-slate-500">{it.contact || "-"}</TableCell>
                <TableCell className="text-slate-500 max-w-md truncate">{it.address || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={()=>isStore?openEdit(it):(setEditing(it),setForm({...empty,...it}),setOpen(true))} data-testid={`btn-edit-${it.id}`}><Pencil className="w-4 h-4"/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(it.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={isStore ? "max-w-3xl" : "max-w-lg"}>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} {label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid={`input-${path}-name`}/></div>
            <div><Label>Kontak (No HP / Email)</Label><Input value={form.contact} onChange={(e)=>setForm({...form,contact:e.target.value})}/></div>
            <div><Label>Alamat</Label><Textarea value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>

            {isStore && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[var(--hotto-border)]">
                <div><Label>Nama PIC / Penanggung Jawab</Label><Input value={form.pic_name || ""} onChange={(e)=>setForm({...form,pic_name:e.target.value})} data-testid="input-store-pic-name"/></div>
                <div><Label>No. WhatsApp PIC</Label><Input value={form.pic_wa || ""} onChange={(e)=>setForm({...form,pic_wa:e.target.value})} data-testid="input-store-pic-wa"/></div>
                <div><Label>Email PIC</Label><Input type="email" value={form.pic_email || ""} onChange={(e)=>setForm({...form,pic_email:e.target.value})} data-testid="input-store-pic-email"/></div>
                <div><Label>Jam Operasional</Label><Input value={form.opening_hours || ""} onChange={(e)=>setForm({...form,opening_hours:e.target.value})} data-testid="input-store-opening-hours"/></div>
                <div className="md:col-span-2"><Label>Catatan Lain / Info Tambahan</Label><Textarea value={form.notes || ""} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="input-store-notes"/></div>
              </div>
            )}

            {isStore && editing && (
              <div className="pt-4 border-t border-[var(--hotto-border)]">
                <div className="mb-2">
                  <Label>Kelola Stok Titipan di Toko</Label>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Ubah qty stok & harga toko untuk tiap produk. Set qty = 0 untuk menghapus stok. Tidak memengaruhi stok gudang.
                  </div>
                </div>
                {stockLoading ? (
                  <div className="p-4 text-center text-slate-500 text-sm">Memuat stok...</div>
                ) : (
                  <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden max-h-80 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50 z-10">
                        <TableRow>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Produk</TableHead>
                          <TableHead className="w-24">Qty</TableHead>
                          <TableHead className="w-36">Harga Toko</TableHead>
                          <TableHead className="w-28 text-right">Nilai</TableHead>
                          <TableHead className="w-14">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editStock.map((s, i) => (
                          <TableRow key={s.product_id} className={s.in_store ? "" : "opacity-70"}>
                            <TableCell className="font-mono-num text-slate-400 text-xs">{i + 1}</TableCell>
                            <TableCell className="font-medium text-sm">{s.product_name}</TableCell>
                            <TableCell>
                              <Input type="number" min={0} value={s.qty}
                                onChange={(e)=>updateEditStockLine(i, "qty", e.target.value)}
                                data-testid={`edit-stock-qty-${i}`} className="h-8 text-sm"/>
                            </TableCell>
                            <TableCell>
                              <Input type="number" min={0} value={s.unit_price}
                                onChange={(e)=>updateEditStockLine(i, "unit_price", e.target.value)}
                                data-testid={`edit-stock-price-${i}`} className="h-8 text-sm"/>
                            </TableCell>
                            <TableCell className="text-right font-mono-num text-xs">
                              {formatIDR(Number(s.qty || 0) * Number(s.unit_price || 0))}
                            </TableCell>
                            <TableCell>
                              {s.in_store ? (
                                <span className="text-[10px] text-emerald-700 font-semibold">STOK</span>
                              ) : Number(s.qty) > 0 ? (
                                <span className="text-[10px] text-blue-700 font-semibold">BARU</span>
                              ) : (
                                <span className="text-[10px] text-slate-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {isStore && !editing && (
              <div className="pt-4 border-t border-[var(--hotto-border)]">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label>Stok Awal di Toko (Opening Balance)</Label>
                    <div className="text-xs text-slate-500 mt-0.5">Produk yang sudah ada di toko ini. Tidak memengaruhi kas atau stok gudang.</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={addStockLine} data-testid="btn-add-init-stock"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
                </div>
                {form.initial_stock?.length > 0 && (
                  <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Produk</TableHead>
                          <TableHead className="w-24">Qty</TableHead>
                          <TableHead className="w-32">Harga Toko</TableHead>
                          <TableHead className="w-24 text-right">Nilai</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form.initial_stock.map((l, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Select value={l.product_id} onValueChange={(v)=>updateStockLine(i,"product_id",v)}>
                                <SelectTrigger data-testid={`init-select-${i}`}><SelectValue placeholder="Pilih produk"/></SelectTrigger>
                                <SelectContent>{products.map((pp)=><SelectItem key={pp.id} value={pp.id}>{pp.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell><Input type="number" value={l.qty} onChange={(e)=>updateStockLine(i,"qty",e.target.value)} data-testid={`init-qty-${i}`}/></TableCell>
                            <TableCell><Input type="number" value={l.unit_price} onChange={(e)=>updateStockLine(i,"unit_price",e.target.value)}/></TableCell>
                            <TableCell className="text-right font-mono-num text-sm">{formatIDR(Number(l.qty||0)*Number(l.unit_price||0))}</TableCell>
                            <TableCell><Button size="icon" variant="ghost" onClick={()=>removeStockLine(i)}><Trash2 className="w-3 h-3 text-red-500"/></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid={`btn-save-${path}`}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
